/**
 * useShellDomainAssets - the GM's asset library, at the table (v11.71.0).
 *
 * Only a GM whose account can host has one; for anyone else this is null and
 * nothing is watched. The library's small rows are watched while the GM sits
 * at the table, so every copy can say when a newer version waits.
 *
 * What it offers the map panel: copy a map in (it is copied into the
 * session's own map library under a name the session does not use yet, then
 * loaded), update a copy to the library's newest, read a palette to apply,
 * and save the live map's palette to the library. Stage 2: read a scene for
 * the wings to park (the scenes hook writes it), and copy a character into
 * the session's character library or bring a copy up to date. The table
 * never reads the library to play: these run only when the GM asks.
 */
import { useMemo } from 'react';
import { backend } from '../backend/app';
import useAssetLibrary from './useAssetLibrary';
import useLibraryTactics, { type LibraryTactics } from './useLibraryTactics';
import useLibraryContent, { BUNDLE_KINDS, type LibraryBundle } from './useLibraryContent';

import useArmouryShelves, { type ArmouryShelves } from './useArmouryShelves';
import {
  readCharacterAsset,
  readFileAsset,
  readMapAsset,
  readPaletteAsset,
  readSceneAsset,
  saveMadeAtTable,
  type LibraryAccess,
} from '../assetSession';
import { changeAsset } from '../assetStore';
import { KINDS } from '../engine/assets/kinds';
import { ownedRow } from '../engine/assets/provenance';
import type { AssetRow, Origin } from '../engine/assets/types';
import type { LibraryEntry } from '../engine/battlefield/mapLibrarySync';
import type { Terrain } from '../engine/battlefield/presets';
import type { SurfaceDef } from '../engine/battlefield/zones';
import type { Material } from '../engine/battlefield/voxels';
import type { Character } from '../engine/character/types';
import type { ParkedScene } from '../engine/scenes/types';
import { diag, errorSummary } from '../diagnostics/log';
import { pickCharacters, pickMaps, pushSheet, sessionSheets } from './libraryCopyIn';
import { versionStanding, type StandingSaved } from './standingToLibrary';
import type { SaveBackBody } from '../engine/assets/saveBack';

type Toast = (message: string, opts?: { type?: 'info' | 'error' | 'success' }) => void;

interface Shell {
  isGm: boolean;
  user?: { uid: string; canHost?: boolean } | null;
  session?: { code?: string | null } | null;
  library: LibraryEntry[];
  terrain: Terrain;
  saveMap(name: string): void;
  copyMap(entry: LibraryEntry): void;
  loadMapWithRules(terrain: Terrain): void;
  pushToast: Toast;
  /** The character editor is open: the library's Tactics are read only then. */
  showCharacterEditor?: boolean;
  /** The session's character library (the sync's). */
  sync?: { sessionCharacters: { character: Character }[]; pushCharacter(character: Character): void } | null;
}

interface Palette {
  surfaces: Record<string, SurfaceDef>;
  materials: Record<string, Material>;
}

export interface TableAssets extends LibraryAccess {
  /** Copy maps in (libraryCopyIn); exactly one is also loaded. Never throws: one toast says what landed. */
  pickMaps(rows: AssetRow[]): Promise<void>;
  /** Bring a copy up to the library's newest version (it is not loaded). */
  updateMap(entry: LibraryEntry): Promise<void>;
  readPalette(row: AssetRow): Promise<Palette>;
  /** Build's Save at the table: the session's copy, then a version in the library. */
  saveMap(name: string): void;
  /**
   * Save a terrain kit: the next version of the kit it came from (`origin`, plan armoury), else a new "Terrain kit"
   * in From session. Resolves with the saved version's origin, or null when it could not be saved (a toast says why).
   */
  savePalette(
    surfaces: Record<string, SurfaceDef>,
    materials: Record<string, Material>,
    origin?: Origin | null,
  ): Promise<Origin | null>;
  /** A scene asset for the wings to park: its saved state, fresh ids. Throws a sentence. */
  readScene(row: AssetRow): Promise<ParkedScene>;
  /** The session's character library, for its rows' "newer" marks. */
  characters: Character[];
  /** Copy characters into the session's character library (libraryCopyIn). Never throws: one toast says it. */
  pickCharacters(rows: AssetRow[]): Promise<void>;
  /** Bring a copy up to the library's newest; the roster's people keep their sheets. */
  updateCharacter(copy: Character): Promise<void>;
  /** The wings' Save to my library: a version of the scene's asset (or a new one); resolves with the origin. */
  saveScene(parked: ParkedScene): Promise<Origin>;
  /** After a sheet is saved to the session's characters: its library version, and the copy pointed at it. */
  versionCharacter(sheet: Character): Promise<void>;
  /** The end-of-session save, into the library too (`standingToLibrary`): each person's line, or why not. */
  versionStanding(bodies: readonly SaveBackBody[]): Promise<StandingSaved>;
  /** May this server hold images and handouts? */
  filesAvailable: boolean;
  /** An image or handout asset's file, for the handouts drawer to post. Throws a sentence. */
  readFile(row: AssetRow): Promise<File>;
}

/** Run a library action from the table; a failure is a toast and a log line, never a throw at the click. */
async function attempt(toast: Toast, what: string, run: () => Promise<void>): Promise<void> {
  try {
    await run();
  } catch (err) {
    diag.error('assets', `${what} failed`, errorSummary(err));
    toast(err instanceof Error ? err.message : `Could not ${what}.`, { type: 'error' });
  }
}

async function updateMap(s: Shell, access: LibraryAccess, entry: LibraryEntry): Promise<void> {
  const row = ownedRow(access.lib.assets, entry.origin);
  if (!row) throw new Error(`${entry.name} is no longer in your library.`);
  const terrain = await readMapAsset(access, row);
  const origin = { assetId: row.id, version: row.version };
  s.copyMap({ name: entry.name, terrain: { ...terrain, name: entry.name }, origin });
  s.pushToast(`${entry.name} is now v${row.version} in this session.`, { type: 'success' });
}

async function updateCharacter(s: Shell, access: LibraryAccess, copy: Character): Promise<void> {
  const row = ownedRow(access.lib.assets, copy.origin);
  if (!row) throw new Error(`${copy.name} is no longer in your library.`);
  const sheet = await readCharacterAsset(access, row);
  pushSheet(s, { ...sheet, id: copy.id, origin: { assetId: row.id, version: row.version } });
  s.pushToast(`${sheet.name} is now v${row.version} in this session's characters.`, { type: 'success' });
}

/** An existing card takes the scene's own name; a new card keeps the one it was given (unique in its folder). */
async function renamedTo(saved: AssetRow, name: string, existed: boolean, access: LibraryAccess): Promise<AssetRow> {
  const clean = name.trim();
  if (!existed || !clean || saved.name === clean) return saved;
  return changeAsset(access.uid, saved.id, (r) => ({ ...r, name: clean, updatedAt: Date.now() }));
}

/** The next version of `origin`'s asset, or a new one in "From session <code>". */
const versionOf = (s: Shell, access: LibraryAccess, made: Parameters<typeof saveMadeAtTable>[1]) =>
  saveMadeAtTable(access, made, s.session?.code ?? 'at the table');

async function saveScene(s: Shell, access: LibraryAccess, parked: ParkedScene): Promise<Origin> {
  const { name } = parked.record;
  const made = { kind: 'scene' as const, name, text: KINDS.scene.encode(parked), origin: parked.record.origin };
  // ONE name (2026-10-20): a scene renamed at the table renames its library card on the next save.
  const saved = await renamedTo(await versionOf(s, access, made), name, !!parked.record.origin, access);
  s.pushToast(`${name} saved to your library as ${saved.name} v${saved.version}.`, { type: 'success' });
  return { assetId: saved.id, version: saved.version };
}

/**
 * A sheet saved at the table: its asset is the one the SESSION'S COPY under this sheet id came from - the roster's
 * own sheet was loaded before any save and never carries the origin a save leaves on the copy.
 */
async function versionCharacter(s: Shell, access: LibraryAccess, sheet: Character): Promise<void> {
  const { origin: own, ...plain } = sheet;
  const origin = own ?? sessionSheets(s).find((c) => c.id === sheet.id)?.origin;
  try {
    const made = { kind: 'character' as const, name: sheet.name, text: KINDS.character.encode(plain), origin };
    const saved = await versionOf(s, access, made);
    pushSheet(s, { ...sheet, origin: { assetId: saved.id, version: saved.version } });
    s.pushToast(`Saved here, and in your library as ${saved.name} v${saved.version}.`, { type: 'success' });
  } catch (err) {
    throw new Error(`${sheet.name} is saved in this session, but not in your library: ${(err as Error).message}`);
  }
}

async function savePalette(s: Shell, access: LibraryAccess, palette: Palette, origin?: Origin | null): Promise<Origin> {
  const label = s.session?.code ?? 'at the table';
  const text = KINDS.palette.encode(palette);
  const saved = await saveMadeAtTable(access, { kind: 'palette', name: 'Terrain kit', text, origin }, label);
  const versioned = saved.version > 1;
  s.pushToast(
    versioned
      ? `Saved to your library as ${saved.name} v${saved.version}.`
      : `Saved to your library as "${saved.name}", in From session ${label}.`,
    { type: 'success' },
  );
  return { assetId: saved.id, version: saved.version };
}

/**
 * A save at the table is a library version too (the user's ruling): the copy's own asset gets
 * its next version, a map from nowhere becomes a new asset in "From session <code>"; then the
 * session's entry is pointed at that version, so the GM's own save never reads as "newer".
 */
async function versionAtTable(s: Shell, access: LibraryAccess, name: string): Promise<void> {
  const origin = s.library.find((e) => e.name === name)?.origin;
  const saved = await versionOf(s, access, {
    kind: 'map',
    name,
    text: KINDS.map.encode({ name, terrain: s.terrain }),
    origin,
  });
  s.copyMap({ name, terrain: { ...s.terrain, name }, origin: { assetId: saved.id, version: saved.version } });
  s.pushToast(`Saved here, and in your library as ${saved.name} v${saved.version}.`, { type: 'success' });
}

/** Build's Save at the table: the session's copy first, as it always was; then the library's version. */
function saveAtTable(s: Shell, access: LibraryAccess) {
  return (name: string) => {
    s.saveMap(name);
    void attempt(s.pushToast, 'save the map to your library', async () => {
      try {
        await versionAtTable(s, access, name);
      } catch (err) {
        throw new Error(`${name} is saved in this session, but not in your library: ${(err as Error).message}`);
      }
    });
  };
}

function tableAssets(s: Shell, access: LibraryAccess): TableAssets {
  return {
    ...access,
    pickMaps: (rows) => pickMaps(s, access, rows),
    updateMap: (entry) => attempt(s.pushToast, 'update the copy', () => updateMap(s, access, entry)),
    readPalette: (row) => readPaletteAsset(access, row),
    saveMap: saveAtTable(s, access),
    savePalette: async (surfaces, materials, origin) => {
      let saved: Origin | null = null;
      await attempt(s.pushToast, 'save the terrain kit', async () => {
        saved = await savePalette(s, access, { surfaces, materials }, origin);
      });
      return saved;
    },
    readScene: (row) => readSceneAsset(access, row),
    characters: sessionSheets(s),
    pickCharacters: (rows) => pickCharacters(s, access, rows),
    updateCharacter: (copy) => attempt(s.pushToast, 'update the copy', () => updateCharacter(s, access, copy)),
    saveScene: (parked) => saveScene(s, access, parked),
    versionCharacter: (sheet) =>
      attempt(s.pushToast, 'save the sheet to your library', () => versionCharacter(s, access, sheet)),
    versionStanding: (bodies) => versionStanding(s, access, bodies),
    filesAvailable: backend.files.available,
    readFile: (row) => readFileAsset(access, row),
  };
}

/** The account whose library this table may use: the GM's, when it can host; null for everyone else. */
const hostUid = (s: Shell): string | null => (s.isGm && s.user && s.user.canHost !== false ? s.user.uid : null);

/** The GM's library as the table reads it: the shelves' "My library", and the Tactics for the Team tab. */
function useTableLibrary(s: Shell, uid: string | null, lib: LibraryAccess['lib'] | null) {
  const access = useMemo(() => (uid && lib ? { uid, lib, backendName: backend.name } : null), [uid, lib]);
  const armouryShelves = useArmouryShelves(access, s.session?.code ?? 'at the table', s.pushToast);
  // Read only while the character editor is open: the kill-team block and the Effects tab are their readers.
  const libraryTactics = useLibraryTactics(access, !!s.showCharacterEditor);
  const libraryBundles = useLibraryContent(access, BUNDLE_KINDS, !!s.showCharacterEditor);
  return { armouryShelves, libraryTactics, libraryBundles };
}

export default function useShellDomainAssets(s: Shell): {
  tableAssets: TableAssets | null;
  armouryShelves: ArmouryShelves | null;
  libraryTactics: LibraryTactics[] | null;
  libraryBundles: LibraryBundle[] | null;
} {
  const uid = hostUid(s);
  const { lib } = useAssetLibrary(uid);
  const { armouryShelves, libraryTactics, libraryBundles } = useTableLibrary(s, uid, lib);
  const { library, terrain, saveMap, copyMap, loadMapWithRules, pushToast } = s;
  const code = s.session?.code;
  // The sync's object is new each render; its two members are what change.
  const sessionCharacters = s.sync?.sessionCharacters;
  const pushCharacter = s.sync?.pushCharacter;
  const tableAssetsValue = useMemo(() => {
    if (!uid || !lib) return null;
    const sync = sessionCharacters && pushCharacter ? { sessionCharacters, pushCharacter } : null;
    const scope = { isGm: true, library, terrain, saveMap, copyMap, loadMapWithRules, pushToast, sync };
    return tableAssets({ ...scope, session: { code } }, { uid, lib, backendName: backend.name });
  }, [
    uid,
    lib,
    library,
    terrain,
    saveMap,
    copyMap,
    loadMapWithRules,
    pushToast,
    sessionCharacters,
    pushCharacter,
    code,
  ]);
  return { tableAssets: tableAssetsValue, armouryShelves, libraryTactics, libraryBundles };
}
